import { Button, Flex, Grid, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronRight, IconCloudDownload, IconCopy, IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import ProductFeedExportModal from '../Modals/ProductFeedExportModal';
import Spinner from '../Spinner';
import ProductFeedFormFields from './ProductFeedFormFields';

const ProductFeedForm = ({ productFeed: passedProductFeed, deleteProductFeed, saveProductFeed, cancel }) => {
  const { accountId, domainId } = useParams();

  const navigate = useNavigate();
  const fetch = useFetch();
  const [error, setError] = useState();
  const tabs = ['general', 'boostsAndFiltrs'];
  const [tab, setTab] = useState(tabs[0]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [productFeed, setProductFeed] = useState(passedProductFeed);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [segments, setSegments] = useState();

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments?scope=internal`)
      .then(({ segments }) => {
        setSegments(segments);
      })
      .catch(setError);
  }, [fetch, accountId, domainId]);

  const create = (productFeed) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages/${productFeed.pageId}/productFeeds`, {
      method: 'POST',
      body: JSON.stringify({ ...productFeed, templateId: productFeed.templateId || null }),
    })
      .then(({ productFeed }) => {
        navigate(`/accounts/${accountId}/domains/${domainId}/pages?type=productFeeds&page=${productFeed.pageId}`);
      })
      .catch(setError);
  };

  if (!segments) {
    return <Spinner />;
  }

  if (productFeed === null) {
    return <ErrorMessage message="Product feed does not exist!" />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  let deleteButton;
  if (typeof deleteProductFeed === 'function') {
    deleteButton = (
      <LockableButton restrictionKey="productFeedsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  let copyButton;
  let exportButton;
  if (productFeed.id) {
    copyButton = (
      <LockableButton
        restrictionKey="productFeedsCreate"
        color="cyan"
        onClick={() => navigate(`/accounts/${accountId}/domains/${domainId}/productFeeds/new?from=${productFeed.id}`)}
      >
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );

    exportButton = (
      <Button variant="default" onClick={() => setExportModalOpen(true)}>
        <IconCloudDownload size={14} /> Export ranked products from feed
      </Button>
    );
  }

  return (
    <>
      <ProductFeedExportModal
        isOpen={exportModalOpen}
        close={() => setExportModalOpen(false)}
        scheduled={() => {
          setExportModalOpen(false);
          notifications.show({
            title: 'Export Scheduled',
            message:
              'Your export has been scheduled for processing. You will receive an email with the export once it has been processed.',
            color: 'success',
          });
        }}
        productFeed={productFeed}
        segments={segments}
      />
      <Grid.Col span={{ base: 12, md: 12 }} ta="left">
        <form>
          <ProductFeedFormFields productFeed={productFeed} setProductFeed={setProductFeed} tab={tab} setTab={setTab} />

          <Flex direction="column" gap="sm">
            <Group justify="flex-end" gap="xs">
              {exportButton}
            </Group>
            <Group justify="flex-end" gap="xs" py="md">
              {productFeed.id || tabs.indexOf(tab) === 0 ? (
                <Button variant="default" onClick={() => cancel()}>
                  Cancel
                </Button>
              ) : (
                <Button variant="default" onClick={() => setTab(tabs[tabs.indexOf(tab) - 1])}>
                  Back
                </Button>
              )}
              {deleteButton}
              {copyButton}
              {productFeed.id || tabs.indexOf(tab) === tabs.length - 1 ? (
                <LockableButton
                  restrictionKey={`productFeeds${productFeed.id ? 'Update' : 'Create'}`}
                  disabled={
                    !productFeed.name ||
                    !productFeed.scope ||
                    productFeed.algorithm === undefined ||
                    productFeed.algorithm === '' ||
                    (productFeed.algorithm === 'latestEventProducts' && !productFeed.contextAction)
                  }
                  onClick={() => (saveProductFeed ? saveProductFeed(productFeed) : create(productFeed))}
                  data-tour="saveBtnProductFeed"
                >
                  <IconDeviceFloppy size={14} /> Save
                </LockableButton>
              ) : (
                <Button
                  onClick={() => {
                    setTab(tabs[tabs.indexOf(tab) + 1]);
                    window.scrollTo(0, 0);
                  }}
                >
                  Next <IconChevronRight size={14} />
                </Button>
              )}
            </Group>
          </Flex>
        </form>
        <ConfirmationModal
          show={confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => deleteProductFeed(cb)}
          message="This action cannot be undone!"
        />
      </Grid.Col>
    </>
  );
};

export default ProductFeedForm;
